/** @forge-header
 * @id          esportsParser
 * @role        Esports ticker parser | CS2/Dota2/Valorant/LoL game moneylines
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/types:ParsedTicker,TennisMatchInfo | kalshiTicker/helpers:parseFallback,splitCompetitors,stripTimePrefix
 * @downstream  kalshiTicker/parse
 * @exports     parseEsportsGame:fn
 * @test        parse.test.ts (indirect)
 */

import type { ParsedTicker, TennisMatchInfo } from '../types';
import { parseDateFromTicker } from '../types';
import { parseFallback, splitCompetitors, stripTimePrefix } from '../helpers';

// Title shown in labels, keyed by the game series prefix.
const ESPORTS_TITLES: Array<[string, string]> = [
  ['KXCS2GAME', 'CS2'],
  ['KXDOTA2GAME', 'Dota 2'],
  ['KXVALORANTGAME', 'Valorant'],
  ['KXLOLGAME', 'LoL'],
];

/**
 * Esports game: KXCS2GAME-26MAY281700IMPSHIN-IMP
 * Team-vs-team moneyline with an HHMM start-time prefix after the date and
 * variable-length org codes. One parser handles every esports title; the
 * title is derived from the matched series prefix. Org codes are displayed
 * as-is (uppercase) — they're acronyms (FNC, NAVI, OG, LGD), not names.
 */
export function parseEsportsGame(raw: string): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 2) return parseFallback(raw);

  const entry = ESPORTS_TITLES.find(([prefix]) => raw.startsWith(prefix));
  const series = entry?.[0] ?? 'KXESPORTS';
  const title = entry?.[1] ?? 'Esports';

  const mid = parts[1] ?? '';
  if (mid.length < 9) return parseFallback(raw);

  const date = parseDateFromTicker(mid.slice(0, 7));
  const outcome = parts[2] ?? null;
  const [c1, c2] = splitCompetitors(stripTimePrefix(mid.slice(7)), outcome);

  const fight: TennisMatchInfo = {
    player1: { playerCode: c1, displayName: c1 },
    player2: { playerCode: c2, displayName: c2 },
  };

  const shortLabel = outcome ? `${title}: ${outcome} Win` : title;
  const fullLabel = `${title}: ${c1} vs ${c2}${outcome ? ` · ${outcome} Win` : ''}`;

  return {
    raw,
    category: 'esports-game',
    series,
    date,
    game: null,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    fight,
    outcome,
    shortLabel,
    fullLabel,
  };
}
